import { readNdjsonStream } from './ndjson.js';
import type {
  AnswerResponse,
  ChoicesResponse,
  DictEntryWithExamples,
  ImportResult,
  InitStreamLine,
  ParsedQuery,
  PersonalListResponse,
  PersonalStats,
  Prefs,
  QuizDirection,
  RecentSession,
  ReviewAnswerResponse,
  ReviewQueueResponse,
  SessionState,
} from './types.js';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, init);
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body (e.g. opaque proxy error). Fall through.
    }
  }
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`;
    const message = (body as { message?: string } | null)?.message ?? res.statusText;
    throw new ApiError(res.status, code, message);
  }
  return body as T;
}

export async function fetchRecentSessions(limit = 5): Promise<RecentSession[]> {
  const data = await jsonFetch<{ items: RecentSession[] }>(
    `/api/sessions/recent?limit=${limit}`,
  );
  return data.items;
}

export async function fetchSession(contentId: string): Promise<SessionState> {
  return jsonFetch<SessionState>(`/api/session/${encodeURIComponent(contentId)}`);
}

export async function fetchDictionary(word: string): Promise<DictEntryWithExamples> {
  return jsonFetch<DictEntryWithExamples>(`/api/dictionary/${encodeURIComponent(word)}`);
}

export async function postAnswer(
  contentId: string,
  body: { word: string; quality: 0 | 1 | 2 | 3 | 4 | 5; mode: 'test' | 'learn' | 'review' },
  signal?: AbortSignal,
): Promise<AnswerResponse> {
  return jsonFetch<AnswerResponse>(`/api/session/${encodeURIComponent(contentId)}/answer`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
}

export type BatchAnswerResult = {
  word: string;
  lemma: string;
  familiarity: number;
  familiarity_before: number;
  next_review_at: number | null;
};

export type BatchAnswersResponse = {
  recorded: number;
  results: BatchAnswerResult[];
  next_session: SessionState;
};

export async function postAnswersBatch(
  contentId: string,
  body: {
    mode: 'test' | 'learn';
    answers: Array<{ word: string; quality: 0 | 1 | 2 | 3 | 4 | 5 }>;
  },
  signal?: AbortSignal,
): Promise<BatchAnswersResponse> {
  return jsonFetch<BatchAnswersResponse>(
    `/api/session/${encodeURIComponent(contentId)}/answers`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    },
  );
}

export async function postFinish(contentId: string): Promise<void> {
  await jsonFetch<{ finished: boolean }>(
    `/api/session/${encodeURIComponent(contentId)}/finish`,
    { method: 'POST' },
  );
}

export async function deleteSession(contentId: string): Promise<void> {
  await jsonFetch<{ deleted: boolean }>(
    `/api/session/${encodeURIComponent(contentId)}`,
    { method: 'DELETE' },
  );
}

export async function fetchChoices(
  word: string,
  direction: QuizDirection,
): Promise<ChoicesResponse> {
  return jsonFetch<ChoicesResponse>(
    `/api/dictionary/${encodeURIComponent(word)}/choices?direction=${direction}`,
  );
}

export async function fetchReviewToday(limit = 50): Promise<ReviewQueueResponse> {
  return jsonFetch<ReviewQueueResponse>(`/api/review/today?limit=${limit}`);
}

export async function postReviewAnswer(
  body: { word: string; quality: 0 | 1 | 2 | 3 | 4 | 5 },
  signal?: AbortSignal,
): Promise<ReviewAnswerResponse> {
  return jsonFetch<ReviewAnswerResponse>('/api/review/answer', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
}


// Streaming init. Resolves when the stream ends (done event arrives or stream
// closes). Calls onEvent for each progress / done / error line. The promise
// itself never throws on transport errors during streaming — caller observes
// errors via onEvent. Network failures before the stream starts DO throw.
export async function initContent(
  query: ParsedQuery & { ok: true },
  onEvent: (line: InitStreamLine) => void,
  signal?: AbortSignal,
): Promise<void> {
  const body =
    query.type === 'movie'
      ? { title: query.title, type: 'movie' as const }
      : {
          title: query.title,
          type: 'tv' as const,
          season: query.season,
          episode: query.episode,
        };
  const res = await fetch('/api/content/init', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'application/x-ndjson',
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    // Treat synchronous failures (4xx validation) as a single error event.
    let message = res.statusText;
    let code = `http_${res.status}`;
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      if (data.error) code = data.error;
      if (data.message) message = data.message;
    } catch {
      // ignore
    }
    onEvent({ error: { status: res.status, code, message } });
    return;
  }
  await readNdjsonStream<InitStreamLine>(res.body, onEvent, signal);
}

// ---- M6: Settings & personal vocab -------------------------------------

export async function fetchPrefs(): Promise<Prefs> {
  return jsonFetch<Prefs>('/api/prefs');
}

export async function putPrefs(patch: Partial<Prefs>): Promise<Prefs> {
  return jsonFetch<Prefs>('/api/prefs', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function fetchPersonalList(opts: {
  q?: string;
  familiarity?: number[];
  sort?: 'recent' | 'alpha' | 'due';
  limit?: number;
  offset?: number;
}): Promise<PersonalListResponse> {
  const params = new URLSearchParams();
  if (opts.q && opts.q.length > 0) params.set('q', opts.q);
  if (opts.familiarity && opts.familiarity.length > 0) {
    params.set('familiarity', opts.familiarity.join(','));
  }
  if (opts.sort) params.set('sort', opts.sort);
  if (typeof opts.limit === 'number') params.set('limit', String(opts.limit));
  if (typeof opts.offset === 'number') params.set('offset', String(opts.offset));
  const qs = params.toString();
  return jsonFetch<PersonalListResponse>(`/api/personal${qs ? `?${qs}` : ''}`);
}

export async function patchPersonal(
  word: string,
  body: { familiarity: 0 | 1 | 2 | 3 | 4 | 5 },
): Promise<unknown> {
  return jsonFetch<unknown>(`/api/personal/${encodeURIComponent(word)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deletePersonal(word: string): Promise<unknown> {
  return jsonFetch<unknown>(`/api/personal/${encodeURIComponent(word)}`, {
    method: 'DELETE',
  });
}

export async function fetchPersonalStats(): Promise<PersonalStats> {
  return jsonFetch<PersonalStats>('/api/personal/stats');
}

export async function postImport(
  payload: unknown,
  mode: 'merge' | 'replace',
): Promise<ImportResult> {
  return jsonFetch<ImportResult>(`/api/import?mode=${mode}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
